import {
  AptunoButton,
  HeaderSimple,
  Icon,
  Input,
  RadioButton,
  SIZES,
  Stepper,
  TextArea,
  VARIANTS,
} from '@aptuno/aptuno-ui';
import { DateInput } from '@components/commons/DatePicker/DatePicker';
import {
  ApplicantCharacterizationFormData,
  ApplicationStateSteps,
  ContractType,
  EmploymentSituationName,
  estateApplicationStageContext,
  EstateApplicationSteps,
  moralRiskStudyToBackModel,
  useCreditStudyActions,
  useDocumentationActions,
  useEstateActions,
  useMoralRiskStudyActions,
} from '@contexts/Estate';
import { ApplicationPersonalCharacteristicsCompleted } from '@contexts/Estate/EstateApplication/domain/events/application-personal-characteristics-completed.event';
import {
  AptunoError,
  EmploymentSituation,
  EmploymentSituationOptions,
  errorMessage,
  errorMessages,
  GeneralAptunoErrorType,
  HighestAcademicLevel,
  HighestAcademicLevelOptions,
  MaritalStatusOptions,
  MaritalStatusTypes,
  Option,
  Response,
  TypeOfContractOptions,
  UserContext,
} from '@contexts/Shared';
import { sumYearToDate, useTrackingSource } from '@contexts/Shared/utils';
import { useAnalyticsTracker } from '@services/analytics';
import { environment } from '@shared/environment/environment';
import { option } from 'fp-ts';
import { Option as Opt } from 'fp-ts/Option';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';

import { SpecifyDocumentationTypeCommand } from '../../../domain';
import { creditStudyErrors } from '../../errors';
import { BtnActions, ViewError } from '../../Types';
import { EaseInModalError } from '../EaseInModal/EaseInModal';
import {
  ActionsBarRoot,
  ContentBox,
  FormWrapper,
  IconClose,
  InputGroup,
  ProcessContainer,
  SelectContainer,
  TextSecondary,
} from '../EstateApplication.styled';
import dynamic from 'next/dynamic';

const Select = dynamic(
  () => import('react-functional-select').then((mod) => mod.Select),
  { ssr: false }
);

const registerOptions = {
  civilStatus: { required: true },
  highestAcademicLevel: { required: true },
  employmentSituation: { required: true },
  incomeLevel: { required: true },
  rentDeclaration: { required: true },
  economicDependents: { required: true },
  businessName: { required: true, maxLength: 80 },
  typeOfContract: { required: true },
  incomingSource: { required: true, maxLength: 300 },
  dateOfBirth: { required: true },
  payRentsInAdvance: { required: true },
  numberPayRentsInAdvance: { required: true, min: 1, max: 12 },
};

const ApplicantCharacterizationScreen = ({ isIsolatedPage = false }) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ApplicantCharacterizationFormData>({
    mode: 'onBlur',
  });

  const estateApplication = useContext(estateApplicationStageContext);
  const currentStep = 1;

  const router = useRouter();
  const originSource = useTrackingSource();
  const eventTracker = useAnalyticsTracker();
  const [formData, setFormData] = useState<
    Opt<ApplicantCharacterizationFormData>
  >(option.none);

  useEffect(() => {
    if (option.isSome(formData)) {
      onSubmit(formData.value);
    }
  }, [formData._tag]);

  useEffect(() => {
    if (router.query?.estateLegalId) {
      getEstateListingDetails({
        estateId: router.query.estateLegalId.toString(),
      });
    }
  }, [router.query?.estateLegalId]);

  const user = useContext(UserContext);

  const { estateListingDetails: estateDetailsData, getEstateListingDetails } =
    useEstateActions().listingDetailsQueryActions.useGetEstateListingDetails();

  const { result: userCreditStudyResponse, recall: getUserCreditStudy } =
    useCreditStudyActions().creditStudyQueryActions.useGetUserCreditStudy();
  const {
    result: addUserInfoCreditStudyResult,
    executing: executingAddUserInfoToCreditStudy,
    execute: addUserInfoCreditStudy,
  } = useCreditStudyActions().creditStudyMutationActions.useAddProvidedUserInfo();

  const {
    result: specifyDocumentationTypeResult,
    executing: executingSpecifyDocumentationType,
    execute: specifyDocumentationType,
  } = useDocumentationActions().documentsCommandActions.useSpecifyDocumentationType();
  const { result: exposedDocumentation, get: getExposedDocumentation } =
    useDocumentationActions().documentQueryActions.useGetExposedDocumentation();

  const {
    initializedMoralRiskStudy,
    loading: loadingInitializeMoralStudy,
    initializeMoralRiskStudy,
  } = useMoralRiskStudyActions().moralRiskStudyCommandActions.useInitializeMoralRiskStudy();

  const handleOnBackClick = () => {
    router.reload();
  };

  const onSubmit = (formData: ApplicantCharacterizationFormData) => {
    const userData = user?.data;

    if (
      userData &&
      Response.isSucceed(userCreditStudyResponse) &&
      !!exposedDocumentation
    ) {
      const addProvidedUserInfoToCreditStudyCommand =
        ApplicantCharacterizationFormData.asAddProvidedUserInfoToCreditStudyCommand(
          userCreditStudyResponse.result.id,
          formData
        );

      if (
        !executingAddUserInfoToCreditStudy &&
        Response.isNotPerformed(addUserInfoCreditStudyResult)
      )
        addUserInfoCreditStudy(addProvidedUserInfoToCreditStudyCommand);

      if (!loadingInitializeMoralStudy && !initializedMoralRiskStudy)
        initializeMoralRiskStudy(
          moralRiskStudyToBackModel(formData.dateOfBirth)
        );

      if (
        !executingSpecifyDocumentationType &&
        Response.isNotPerformed(specifyDocumentationTypeResult) &&
        Response.isSucceed(exposedDocumentation)
      )
        specifyDocumentationType(
          new SpecifyDocumentationTypeCommand(
            exposedDocumentation.result.id,
            addProvidedUserInfoToCreditStudyCommand.declaresRent.value,
            addProvidedUserInfoToCreditStudyCommand.employmentSituation
          )
        );
    }
  };

  const [showErrorModal, setShowErrorModal] = useState<ViewError | undefined>(
    undefined
  );

  useEffect(() => {
    getUserCreditStudy();
  }, []);

  useEffect(() => {
    if (Response.isSucceed(userCreditStudyResponse))
      getExposedDocumentation({
        creditStudyId: userCreditStudyResponse.result.id,
      });
  }, [userCreditStudyResponse.type]);

  useEffect(() => {
    if (Response.isError(addUserInfoCreditStudyResult)) {
      const errorCode = addUserInfoCreditStudyResult.error['code'];

      setShowErrorModal(
        creditStudyErrors[errorCode] || {
          ...creditStudyErrors['DEFAULT'],
          text: errorCode,
        }
      );
    }

    if (
      Response.isError(userCreditStudyResponse) &&
      !GeneralAptunoErrorType.isResourceNotFound(
        userCreditStudyResponse.error as AptunoError
      )
    ) {
      const errorCode = (userCreditStudyResponse.error as AptunoError).code;

      setShowErrorModal(
        creditStudyErrors[errorCode] || {
          ...creditStudyErrors['DEFAULT'],
          text: errorCode,
        }
      );
    }
  }, [addUserInfoCreditStudyResult.type, userCreditStudyResponse.type]);

  useEffect(() => {
    if (
      Response.isSucceed(specifyDocumentationTypeResult) &&
      Response.isSucceed(addUserInfoCreditStudyResult) &&
      !!initializedMoralRiskStudy
    ) {
      segmentTrack();
      changeToNextStep();
    }
  }, [
    addUserInfoCreditStudyResult.type,
    initializedMoralRiskStudy,
    specifyDocumentationTypeResult.type,
  ]);

  const changeToNextStep = () => {
    /*     setShowErrorModal(undefined); */
    estateApplication &&
      estateApplication.setCurrentState(ApplicationStateSteps.documentsUpload);
  };

  const handleOnLeave = () => {
    estateApplication &&
      estateApplication.setCurrentState(ApplicationStateSteps.leaveApplication);
  };

  const getCivilStatusOptionValue = useCallback(
    (option: Option<MaritalStatusTypes>): string => option.value,
    []
  );
  const getDniTypeOptionLabel = useCallback(
    (option: Option<MaritalStatusTypes>): string => `${option.name}`,
    []
  );

  const getAcademicLevelOptionValue = useCallback(
    (option: Option<HighestAcademicLevel>): string => option.value,
    []
  );
  const getAcademicLevelOptionLabel = useCallback(
    (option: Option<HighestAcademicLevel>): string => `${option.name}`,
    []
  );

  const getEmploymentSituationOptionValue = useCallback(
    (option: Option<EmploymentSituationName>): string => option.value,
    []
  );
  const getEmploymentSituationOptionLabel = useCallback(
    (option: Option<EmploymentSituation>): string => `${option.name}`,
    []
  );
  const isLoadingState =
    executingAddUserInfoToCreditStudy || loadingInitializeMoralStudy;
  const getTypeOfContractOptionValue = useCallback(
    (option: Option<ContractType>): string => option.value,
    []
  );
  const getTypeOfContractOptionLabel = useCallback(
    (option: Option<ContractType>): string => `${option.name}`,
    []
  );
  const [typeOfEmployee, setTypeOfEmployee] = useState<
    EmploymentSituationName | undefined
  >(undefined);
  const [showNumbersOfRents, setShowNumbersOfRents] = useState<boolean>(false);

  const segmentTrack = () =>
    user?.data?.id &&
    eventTracker.trackEvent(
      new ApplicationPersonalCharacteristicsCompleted(
        originSource,
        user.data.id
      )
    );

  const getActionAfterError = (error: ViewError) => {
    switch (error.btnAction) {
      case BtnActions.GO_BACK:
        handleOnBackClick();
        break;
      case BtnActions.GO_NEXT:
        changeToNextStep();
        break;
      default:
        setShowErrorModal(undefined);
        break;
    }
  };

  const modal = () => {
    return (
      showErrorModal && (
        <EaseInModalError
          size={600}
          isActive={showErrorModal}
          onClose={() => getActionAfterError(showErrorModal)}
          title={showErrorModal.title}
          onAcceptButtonClick={() => getActionAfterError(showErrorModal)}
          btnText={`${showErrorModal.btnText}`}
          mainText={showErrorModal.text}
          secondaryText={showErrorModal.textSecondary}
        />
      )
    );
  };

  const renderSourceOfIncome = (state: EmploymentSituationName) => {
    switch (state) {
      case EmploymentSituationName.EMPLOYED:
        return <EmployeeForm />;
      case EmploymentSituationName.INDEPENDENT:
      case EmploymentSituationName.LEGAL_PERSON:
      case EmploymentSituationName.RENTIER:
      case EmploymentSituationName.UNEMPLOYED:
      case EmploymentSituationName.PENSIONER:
      default:
        return <NotEmployeeForm />;
    }
  };

  const EmployeeForm = () => (
    <>
      {showErrorModal && modal()}
      <InputGroup>
        <label>Escribe la razón social de la empresa</label>
        <Input
          type="text"
          placeholder="Ejemplo: Aptuno SAS"
          disabled={isLoadingState}
          {...register('businessName', registerOptions.businessName)}
          invalid={!!errors.businessName}
        />
        {errors.businessName?.type === 'required' &&
          errorMessage(`La razón social ${errorMessages.requiredAlt}`)}
        {errors.businessName?.type === 'maxLength' &&
          errorMessage(
            `El nombre de la empresa ${errorMessages.maxLength}. (80)`
          )}
      </InputGroup>
      <InputGroup>
        <label>¿Qué tipo de contrato tienes con tu empleador?</label>
        <Controller
          control={control}
          {...register('typeOfContract', registerOptions.typeOfContract)}
          render={({ field: { onChange, value } }) => (
            <SelectContainer invalid={!!errors.typeOfContract}>
              <Select
                isSearchable
                placeholder="Selecciona uno"
                options={TypeOfContractOptions}
                initialValue={value}
                onOptionChange={onChange}
                getOptionValue={getTypeOfContractOptionValue}
                getOptionLabel={getTypeOfContractOptionLabel}
                isDisabled={isLoadingState}
              />
            </SelectContainer>
          )}
        />
        {errors.typeOfContract?.type === 'required' &&
          errorMessage(`El tipo de contrato ${errorMessages.required}. (50)`)}
      </InputGroup>
    </>
  );

  const NotEmployeeForm = () => (
    <>
      <InputGroup>
        <label>Describe brevemente la fuente de tus ingresos.</label>
        <TextArea
          id="incomingSource"
          type="number"
          maxLength={300}
          placeholder="Cuéntanos un poco más acerca de tu trabajo, de tu industria o del origen de tus ingresos."
          {...register('incomingSource', registerOptions.incomingSource)}
          invalid={!!errors.incomingSource}
          disabled={isLoadingState}
        />
        {errors.incomingSource?.type === 'required' &&
          errorMessage(`La fuente de ingresos ${errorMessages.requiredAlt}`)}
        {errors.incomingSource?.type === 'maxLength' &&
          errorMessage(
            `La fuente de ingresos ${errorMessages.maxLength}. (300)`
          )}
      </InputGroup>
      <TextSecondary>
        Esto nos da más claridad acerca de tu perfil laboral para arrendar con
        Aptuno.
      </TextSecondary>
    </>
  );

  return (
    <div>
      <HeaderSimple
        withBorderRadius={false}
        onButtonBack={handleOnBackClick}
        maxWidth="100%"
        isFixed={false}
      >
        <h3>Estudio de Riesgo</h3>
        <IconClose
          icon={'closeAlt'}
          color={'#9B9B9B'}
          marginRight={'16px !important'}
          onClick={handleOnLeave}
        />
      </HeaderSimple>
      <ProcessContainer>
        <Stepper steps={EstateApplicationSteps} activeStep={currentStep} />
      </ProcessContainer>
      <ContentBox>
        <FormWrapper
          id={'lifeInfo'}
          onSubmit={handleSubmit((data) => {
            if (option.isNone(formData)) {
              setFormData(() => option.fromNullable(data));
            }
          })}
        >
          <InputGroup>
            <label>Fecha de Nacimiento</label>
            <Controller
              control={control}
              {...register('dateOfBirth', registerOptions.dateOfBirth)}
              render={({ field }) => (
                <DateInput
                  {...field}
                  disabled={isLoadingState}
                  invalid={!!errors.dateOfBirth}
                  maxDate={sumYearToDate(new Date(), -18)}
                  minDate={new Date(1922, 1, 1)}
                />
              )}
            />
            {errors.dateOfBirth?.type === 'required' &&
              errorMessage(
                `La fecha de nacimiento ${errorMessages.requiredAlt}`
              )}
          </InputGroup>
          <InputGroup>
            <label>¿Cuál es tu estado civil?</label>
            <Controller
              control={control}
              {...register('civilStatus', registerOptions.civilStatus)}
              render={({ field: { onChange, value } }) => (
                <SelectContainer invalid={!!errors.civilStatus}>
                  <Select
                    isSearchable
                    placeholder="Selecciona uno"
                    options={MaritalStatusOptions}
                    initialValue={value}
                    onOptionChange={onChange}
                    getOptionValue={getCivilStatusOptionValue}
                    getOptionLabel={getDniTypeOptionLabel}
                    isDisabled={isLoadingState}
                  />
                </SelectContainer>
              )}
            />
            {errors.civilStatus?.type === 'required' &&
              errorMessage(`El estado civil ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>¿Cuál es tu formación académica más alta?</label>
            <Controller
              control={control}
              {...register(
                'highestAcademicLevel',
                registerOptions.highestAcademicLevel
              )}
              render={({ field }) => (
                <SelectContainer invalid={!!errors.highestAcademicLevel}>
                  <Select
                    isSearchable
                    placeholder="Selecciona uno"
                    options={HighestAcademicLevelOptions}
                    onOptionChange={field.onChange}
                    getOptionValue={getAcademicLevelOptionValue}
                    getOptionLabel={getAcademicLevelOptionLabel}
                    isDisabled={isLoadingState}
                  />
                </SelectContainer>
              )}
            />
            {errors.highestAcademicLevel?.type === 'required' &&
              errorMessage(`El nivel académico ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>¿Cuál es tu situación laboral actual?</label>
            <Controller
              control={control}
              {...register(
                'employmentSituation',
                registerOptions.employmentSituation
              )}
              render={({ field }) => {
                setTypeOfEmployee(field.value?.value);
                return (
                  <SelectContainer invalid={!!errors.employmentSituation}>
                    <Select
                      isSearchable
                      placeholder="Selecciona uno"
                      options={EmploymentSituationOptions}
                      onOptionChange={field.onChange}
                      getOptionValue={getEmploymentSituationOptionValue}
                      getOptionLabel={getEmploymentSituationOptionLabel}
                      isDisabled={isLoadingState}
                    />
                  </SelectContainer>
                );
              }}
            />
            {errors.employmentSituation?.type === 'required' &&
              errorMessage(`La situación laboral ${errorMessages.required}`)}
          </InputGroup>
          {typeOfEmployee && renderSourceOfIncome(typeOfEmployee)}
          <InputGroup>
            <label>¿Cuánto son tus ingresos mensuales disponibles?</label>
            <NumberFormat
              placeholder="Ejemplo: $2.500.000"
              {...register('incomeLevel', registerOptions.incomeLevel)}
              thousandSeparator={environment.thousandSeparator}
              decimalSeparator={environment.decimalSeparator}
              decimalScale={2}
              isNumericString
              prefix="$"
              customInput={Input}
              invalid={!!errors.incomeLevel}
              disabled={isLoadingState}
              onValueChange={(values) => {
                const { value } = values;
                const currentValue = value ? Number(value) : 0;

                setValue('incomeLevel', currentValue);
              }}
            />
            {errors.incomeLevel?.type === 'required' &&
              errorMessage(`El nivel de ingresos ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>¿Declaras renta?</label>
            <RadioButton
              id="yes"
              label="Sí"
              value={1}
              disabled={isLoadingState}
              {...register('rentDeclaration', registerOptions.rentDeclaration)}
            />
            <RadioButton
              id="no"
              label="No"
              value={0}
              disabled={isLoadingState}
              {...register('rentDeclaration', registerOptions.rentDeclaration)}
            />
            {errors.rentDeclaration?.type === 'required' &&
              errorMessage(`Este campo ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>¿Tienes dependientes económicos?</label>
            <RadioButton
              id="yes"
              label="Sí"
              value={1}
              disabled={isLoadingState}
              {...register(
                'economicDependents',
                registerOptions.economicDependents
              )}
            />
            <RadioButton
              id="no"
              label="No"
              value={0}
              disabled={isLoadingState}
              {...register(
                'economicDependents',
                registerOptions.economicDependents
              )}
            />
            {errors.economicDependents?.type === 'required' &&
              errorMessage(`Este campo ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>¿Pagarías canones por adelantado?</label>
            <RadioButton
              id="yesPayRentsInAdvance"
              label="Sí"
              value={1}
              disabled={isLoadingState}
              {...register(
                'payRentsInAdvance',
                registerOptions.payRentsInAdvance
              )}
              onChange={() => setShowNumbersOfRents(true)}
            />
            <RadioButton
              id="noPayRentsInAdvance"
              label="No"
              value={0}
              disabled={isLoadingState}
              {...register(
                'payRentsInAdvance',
                registerOptions.payRentsInAdvance
              )}
              onChange={() => {
                setShowNumbersOfRents(false);
                setValue('numberPayRentsInAdvance', 0);
              }}
            />
            {errors.payRentsInAdvance?.type === 'required' &&
              errorMessage(`Este campo ${errorMessages.required}`)}
          </InputGroup>
          {showNumbersOfRents && (
            <InputGroup>
              <label>¿Cuántos canones pagarías?</label>
              <NumberFormat
                placeholder="Ejemplo: 3"
                {...register(
                  'numberPayRentsInAdvance',
                  registerOptions.numberPayRentsInAdvance
                )}
                customInput={Input}
                invalid={!!errors.numberPayRentsInAdvance}
                disabled={isLoadingState}
                onValueChange={(values) => {
                  const { value } = values;
                  const currentValue = value ? Number(value) : undefined;

                  setValue('numberPayRentsInAdvance', currentValue);
                }}
              />
              {errors.numberPayRentsInAdvance?.type === 'required' &&
                errorMessage(`Este campo ${errorMessages.required}`)}
              {errors.numberPayRentsInAdvance?.type === 'max' &&
                errorMessage(
                  `${errorMessages.max}. (${registerOptions.numberPayRentsInAdvance.max})`
                )}
              {errors.numberPayRentsInAdvance?.type === 'min' &&
                errorMessage(
                  `${errorMessages.min}. (${registerOptions.numberPayRentsInAdvance.min})`
                )}
            </InputGroup>
          )}
        </FormWrapper>
        <ActionsBarRoot>
          <AptunoButton
            appearance={VARIANTS.PRIMARY}
            size={SIZES.BIGGEST}
            type={'submit'}
            form="lifeInfo"
          >
            {isLoadingState ? 'Cargando...' : 'Continuar'}
            <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
          </AptunoButton>
        </ActionsBarRoot>
      </ContentBox>
    </div>
  );
};

export { ApplicantCharacterizationScreen };
